import { mountReportingSurface } from '../core/reportingSurface.js';
import { loadingState, beginRefresh, settleLoadingValues, failLoadingStates } from '../core/loadingState.js';
/**
 * analytics-conversions-route.js — client-saas /analytics/conversions
 *
 * The MODULAR conversions report. Ad Performance answers "what did spend produce"; this
 * page answers "which conversion actions am I willing to call a result, and what do the
 * numbers look like if I change my mind". The action selector is the page: tick or untick
 * an action and every derived figure (conversions, cost per conversion, value, the daily
 * chart, the per-action table) recomputes against that selection.
 *
 * WHY THE RECOMPUTE IS A SERVER ROUND TRIP, not client-side arithmetic over one payload.
 * GET /coreAdMetrics/conversions?actions=… re-runs the same owner-scoped SQL the saved
 * selection uses, so an exploratory selection and a saved one can never produce different
 * totals for the same set of actions. Summing a client-held table would be a second
 * implementation of the same math, and the first one to drift would be the one people
 * trust.
 *
 * EXPLORING IS NOT SAVING. ?actions= is per-request; only "Save as default" writes
 * conversion_action_ids back (POST /integrations/google_ads/conversion_actions, gated
 * server-side on MANAGE_MAGNETS). So a managed-client viewer who cannot manage still gets
 * a fully live selector to explore with, and simply has no save affordance — a read-only
 * page here would have meant a read-only QUESTION, which is the opposite of the point.
 *
 * TWO SIDES, NEVER ONE NUMBER. The tiles/chart/table are Google's attributed conversions.
 * The "Fed by Magnet" panel is our upload ledger (what Magnet pushed INTO Google). They
 * describe the same loop from opposite ends and overlap by design, so they are shown as
 * separate panels and never summed.
 */

import AuthController from '../services/AuthController.js';
import { inPortfolio, pickerPageHTML, initPicker } from '../services/portfolio-view.js';
import { canManage } from '../services/integrations-shared.js';
import { statCard } from '../core/statCard.js';
import { ensureChartStyles, dualAxisChartHTML, wireChartHover, fmtAxis } from '../services/chart-kit.js';
import { capList } from '../core/listExpand.js';
import { guardedFetch, onNavigationDiscard } from '../core/navGeneration.js';
import { isDenial, denialState, failIfNotOk } from '../core/denialSurface.js';

const apiBase = () => AuthController.getConfig?.().BASE_URL || '';
const ymd = (d) => d.toISOString().slice(0, 10);

// '—' is the EMPTY placeholder, not punctuation: a null cost-per-conversion means the
// figure does not exist yet (no spend, or no conversions to divide by), and printing "$0"
// there would be a claim nobody made.
const DASH = '—';
const fmtMoney = (n) => (n == null ? DASH : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
const fmtNum = (n) => (n == null ? DASH : Number(n).toLocaleString());
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

/** Cost per conversion. Null (not zero) whenever the division has no meaning. */
const costPer = (spend, conversions) => {
  const s = Number(spend);
  const c = Number(conversions);
  if (!Number.isFinite(s) || !Number.isFinite(c) || c <= 0) return null;
  return Math.round((s / c) * 100) / 100;
};

/**
 * The four actions Magnet provisions for an org (services/adConversionProvision.js).
 * Mirrored here for LABELLING only — the ids come from the account's stored mapping, and
 * the names are the fallback when an older account has no mapping recorded yet.
 */
const MAGNET_KIND_ORDER = ['magnet_fill', 'qualified_lead', 'call_dni', 'calls_from_ads'];
const MAGNET_KIND_LABEL = {
  magnet_fill: 'Magnet fills',
  qualified_lead: 'Qualified leads',
  call_dni: 'Website calls',
  calls_from_ads: 'Ad calls',
};
const MAGNET_ACTION_NAMES = new Set([
  'Magnet Lead', 'Magnet Qualified Lead', 'Magnet Phone Call (Website)',
  // Renamed 2026-09-14; the old spelling stays so pre-rename metric rows
  // (which carry the name they synced under) still badge as Magnet.
  'Ad Calls (Magnet reporting)', 'Calls from Ads (Magnet)',
]);

const PERIODS = [{ d: 7, label: '7 Days' }, { d: 30, label: '30 Days' }, { d: 90, label: '90 Days' }];
let periodDays = 30;

const state = {
  from: null, to: null,
  spend: null,          // overlay totals.spend, independent of the selection
  universe: [],         // [{ id, name, category, magnet, kind }]
  selection: new Set(),
  conv: null,           // last /conversions payload
  rows: [],             // merged daily rows for the chart
  overlayRows: [],
  canSave: false,
  busy: false,
};

// Release the busy latch when the user navigates away. LOAD-BEARING for navGeneration:
// guardedFetch never settles for an abandoned page, so the `finally` that clears
// state.busy never runs, and this module-level object survives navigation (ES modules are
// singletons). Without this, leaving the page mid-request strands state.busy true and the
// guarded operation is permanently blocked on return.
onNavigationDiscard(() => { state.busy = false; });

let stylesInjected = false;
function ensureStyles() {
  ensureChartStyles();
  if (stylesInjected) return;
  const s = document.createElement('style');
  s.id = 'm5t-conversions-styles';
  s.textContent = `
    .page-shell .conv-note { font-size: 0.76rem; color: var(--color-text-subtle, #6C7C9A); margin: 12px 2px 0; }
    .page-shell .chart-period-btn { cursor: pointer; }
    .page-shell .chart-period-btn.active { background: var(--color-primary-600, #0072BA); color: #fff; }
    .page-shell .conv-group { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); margin: 4px 2px 6px; }
    .page-shell .conv-group + .conv-group, .page-shell .conv-list + .conv-group { margin-top: 16px; }
    .page-shell .conv-row { display: flex; align-items: center; gap: 10px; padding: 7px 8px; border-radius: var(--radius-sm, 6px); cursor: pointer; }
    /* Restated because the author display rule above beats the UA's [hidden] rule,
       and core/listExpand.js hides capped rows with setAttribute('hidden'). Without
       this the cap is a no-op: the control reads "Showing 25 of N" above N visible
       rows. Same failure deliberate-fixed once already at deliverable-fill-route.js. */
    .page-shell .conv-row[hidden] { display: none; }
    .page-shell .conv-row:hover { background: var(--color-surface, #F4F7FB); }
    .page-shell .conv-row input { cursor: pointer; }
    .page-shell .conv-name { font-weight: 600; font-size: 0.88rem; }
    .page-shell .conv-cat { font-size: 0.74rem; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .conv-tag { font-size: 0.62rem; font-weight: 700; letter-spacing: 0.04em; padding: 2px 7px; border-radius: 999px; background: var(--color-accent-soft, rgba(0,114,186,0.1)); color: var(--color-accent-hover, #00558D); }
    .page-shell .conv-saverow { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 14px; }
    .page-shell .conv-savemsg { font-size: 0.8rem; font-weight: 600; color: var(--color-text-muted, #4A5A78); }
    .page-shell .conv-savemsg.err { color: var(--color-danger, #DD4124); }
    .page-shell .conv-wrap { overflow-x: auto; }
    .page-shell .conv-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
    .page-shell .conv-table th { text-align: left; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); padding: 8px 12px; border-bottom: 1px solid var(--color-border, #D4DCE8); white-space: nowrap; }
    .page-shell .conv-table td { padding: 9px 12px; border-bottom: 1px solid var(--color-surface-2, #EAF0F7); vertical-align: middle; }
    .page-shell .conv-table .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .page-shell .conv-table .sub { font-size: 0.78rem; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .conv-busy { opacity: 0.55; transition: opacity .12s ease; }
    html.ff-no-animations .page-shell .conv-busy, html.ff-no-animations .page-shell .conv-row { transition: none; }
    @media (prefers-reduced-motion: reduce) { .page-shell .conv-busy { transition: none; } }
  `;
  document.head.appendChild(s);
  stylesInjected = true;
}

const emptyState = (icon, msg) =>
  `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;

export function render() {
  if (inPortfolio()) {
    return pickerPageHTML({
      section: 'analytics-conversions', title: 'Conversions',
      description: 'Pick a client to view their conversion reporting.',
    });
  }
  ensureStyles();
  state.canSave = canManage();
  return `
    <div class="page-shell" data-section="analytics-conversions">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Conversions</h1>
          <p class="page-description">The Google Ads conversion actions you count as results.</p>
        </div>
        <div class="chart-controls" id="conv-periods">
          ${PERIODS.map((p) => `<button class="chart-period-btn${p.d === periodDays ? ' active' : ''}" type="button" data-days="${p.d}">${p.label}</button>`).join('')}
        </div>
      </div>

      <div class="stats-grid" id="conv-stats">
        ${statCard({ loading: true, icon: 'payments', label: 'Ad Spend', valueId: 'conv-spend', href: '#/ad-accounts' })}
        ${statCard({ loading: true, icon: 'flag', label: 'Conversions', valueId: 'conv-count', sub: 'Reported by Google Ads' })}
        ${statCard({ loading: true, icon: 'request_quote', label: 'Cost / Conversion', valueId: 'conv-cpc' })}
        ${statCard({ loading: true, icon: 'universal_currency_alt', label: 'Conversion Value', valueId: 'conv-value' })}
      </div>

      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">checklist</span> Counted actions</h2>
          <span class="live-indicator" id="conv-selcount"></span>
        </div>
        <div class="card-body" id="conv-selector">
          ${loadingState('list', 'Loading conversion actions')}
        </div>
      </div>

      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">insights</span> Conversions vs Spend</h2>
          <span class="live-indicator" id="conv-range"></span>
        </div>
        <div class="card-body" id="conv-chart">
          ${loadingState('chart', 'Loading conversions')}
        </div>
      </div>

      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">table_rows</span> By conversion action</h2>
          <span class="live-indicator">Reported by Google Ads</span>
        </div>
        <div class="card-body" id="conv-actions">
          ${loadingState('list', 'Loading delivery records')}
        </div>
      </div>

      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">upload</span> Fed by Magnet</h2>
          <span class="live-indicator">Sent by Magnet</span>
        </div>
        <div class="card-body" id="conv-ledger">
          ${loadingState('list', 'Loading delivery records')}
        </div>
      </div>
    </div>`;
}

export async function init() {
  if (inPortfolio()) {
    const fmtN = (n) => Number(n || 0).toLocaleString();
    return initPicker(apiBase(), [
      { h: 'Spend', td: (r) => (r.ad ? `$${fmtN(r.ad.spend)}` : DASH) },
      { h: 'Clicks', td: (r) => (r.ad ? fmtN(r.ad.clicks) : DASH) },
      { h: 'Leads', td: (r) => fmtN(r.leads.window ?? r.leads.total) },
      { h: 'CPL', td: (r) => (r.ad?.cost_per_lead != null ? `$${fmtN(r.ad.cost_per_lead)}` : DASH) },
    ], { emptyMsg: 'No client orgs yet. Connect a client ad account to see conversions here.' });
  }

  mountReportingSurface('conversions');
  const periods = document.getElementById('conv-periods');
  periods?.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-days]');
    if (!btn) return;
    periodDays = Number(btn.dataset.days) || 30;
    periods.querySelectorAll('.chart-period-btn').forEach((b) => b.classList.toggle('active', Number(b.dataset.days) === periodDays));
    load();
  });

  // Delegated on the card body: the selector is re-rendered whole on every period change
  // and capList() re-renders it again on expand, so per-checkbox listeners would be lost.
  document.getElementById('conv-selector')?.addEventListener('change', (e) => {
    const box = e.target.closest('input[type="checkbox"][data-action-id]');
    if (!box) return;
    if (box.checked) state.selection.add(box.value); else state.selection.delete(box.value);
    updateSelCount();
    applySelection();
  });

  await load();
}

// ── loading ──────────────────────────────────────────────────────────────────

async function load() {
  const loadingTarget = document.getElementById('conv-chart');
  const finishRefresh = beginRefresh(loadingTarget, 'Updating conversions');
  const now = new Date();
  state.to = ymd(now);
  state.from = ymd(new Date(now.getTime() - (periodDays - 1) * 86400000));
  const rangeEl = document.getElementById('conv-range');
  if (rangeEl) rangeEl.textContent = `${state.from} → ${state.to}`;

  const base = apiBase();
  const q = `from=${state.from}&to=${state.to}`;
  let overlay = null, conv = null, ledger = null, accounts = [], catalog = null;
  try {
    // The integrations catalog is MANAGE_MAGNETS-gated server-side, so it is only
    // requested for a caller who can manage. A read-only viewer builds the same selector
    // from the account's stored Magnet mapping plus whatever /conversions reports, which
    // are both readable with view_ad_metrics.
    const wantCatalog = canManage();
    const [oRes, cRes, lRes, aRes, catRes] = await Promise.all([
      guardedFetch(`${base}/m5t/v5/coreAdMetrics/overlay?${q}`).then((r) => r.json()).catch(() => null),
      guardedFetch(`${base}/m5t/v5/coreAdMetrics/conversions?${q}`).then((r) => r.json()).catch(() => null),
      guardedFetch(`${base}/m5t/v5/coreAdMetrics/upload-ledger?${q}`).then((r) => r.json()).catch(() => null),
      guardedFetch(`${base}/m5t/v5/coreAdAccount`).then((r) => r.json()).catch(() => null),
      wantCatalog
        ? guardedFetch(`${base}/m5t/v5/integrations/google_ads/conversion_actions`).then((r) => r.json()).catch(() => null)
        : Promise.resolve(null),
    ]);
    overlay = oRes; conv = cRes; ledger = lRes; catalog = catRes;
    accounts = (aRes?.data || []).filter((a) => a.status !== 'DELETED');
  } catch (err) {
    failLoadingStates(loadingTarget?.closest('.page-shell'));
    console.warn('[analytics-conversions] load failed:', err);
    setHTML('conv-chart', emptyState('error', 'Could not load conversions. Please try again.'));
    return;
  } finally {
    finishRefresh();
    settleLoadingValues(loadingTarget?.closest('.page-shell'));
  }

  state.spend = overlay?.totals?.spend ?? null;
  state.overlayRows = overlay?.rows || [];
  state.canSave = canManage();

  const gAccounts = accounts.filter((a) => a.provider === 'google_ads');
  state.universe = buildUniverse({ catalog, conv, accounts: gAccounts });

  // Initial selection: whatever is saved. With nothing saved, the four actions Magnet
  // provisions are the sensible default to open on — they are the ones this product
  // actually feeds — but they are only a VIEW until someone saves them.
  const saved = (conv?.has_selection && Array.isArray(conv.selected_ids)) ? conv.selected_ids.map(String) : [];
  const magnetIds = state.universe.filter((a) => a.magnet).map((a) => a.id);
  state.selection = new Set(saved.length ? saved : magnetIds);

  renderLedger(ledger);
  renderSelector({ accounts: gAccounts });
  updateSelCount();

  if (!gAccounts.length) {
    setText('conv-spend', fmtMoney(state.spend));
    setHTML('conv-chart', emptyState('link_off', 'No ad account connected yet. Your agency can connect Google Ads to see conversions here.'));
    setHTML('conv-actions', emptyState('link_off', 'No ad account connected yet.'));
    return;
  }

  // The saved selection is already what /conversions counted; a defaulted-to-Magnet
  // selection is not, so it has to be asked for explicitly.
  if (saved.length || !state.selection.size) {
    state.conv = conv;
    renderData();
  } else {
    await applySelection();
  }
}

/**
 * The set of actions the selector offers, in one list, from up to three sources:
 *   1. the full Google Ads catalog (manage-capable callers only),
 *   2. whatever /conversions reported for the saved selection,
 *   3. the account's provisioned Magnet actions (account_config.magnet_actions).
 * Source 3 matters even when it has no metrics: a freshly provisioned org must be able to
 * see and tick its own Magnet actions before Google has attributed anything to them.
 */
function buildUniverse({ catalog, conv, accounts }) {
  const byId = new Map();
  const put = (id, patch) => {
    const key = String(id || '');
    if (!key) return;
    const cur = byId.get(key) || { id: key, name: null, category: null, magnet: false, kind: null };
    byId.set(key, { ...cur, ...Object.fromEntries(Object.entries(patch).filter(([, v]) => v != null && v !== false)) });
  };

  (catalog?.data?.actions || []).forEach((a) => put(a.id, { name: a.name, category: a.category }));
  (conv?.actions || []).forEach((a) => put(a.id, { name: a.name, category: a.category }));
  // Every action with synced data (server-built, no live catalog): viewers
  // without MANAGE_MAGNETS never get `catalog`, but still see these.
  (conv?.available || []).forEach((a) => put(a.id, { name: a.name, category: a.category }));

  // Magnet mapping: kind -> { id, name }, recorded per account at provision time.
  accounts.forEach((acct) => {
    const mapping = acct.account_config?.magnet_actions || {};
    Object.entries(mapping).forEach(([kind, m]) => {
      if (!m?.id) return;
      put(m.id, { name: m.name, magnet: true, kind });
    });
  });
  // Older accounts predate the mapping; the provisioned names are stable, so fall back to
  // them rather than showing an unlabelled list.
  byId.forEach((a) => { if (!a.magnet && a.name && MAGNET_ACTION_NAMES.has(a.name)) a.magnet = true; });

  const list = [...byId.values()];
  const rank = (a) => (a.magnet ? MAGNET_KIND_ORDER.indexOf(a.kind) : -1);
  return list.sort((a, b) => {
    if (a.magnet !== b.magnet) return a.magnet ? -1 : 1;
    if (a.magnet) {
      const ra = rank(a), rb = rank(b);
      if (ra !== rb) return (ra < 0 ? 99 : ra) - (rb < 0 ? 99 : rb);
    }
    return String(a.name || a.id).localeCompare(String(b.name || b.id));
  });
}

/**
 * Recompute against the current tick-boxes. One request, same SQL the saved selection
 * runs, so exploring and saving can never disagree. An empty selection short-circuits:
 * there is nothing to ask the server for, and the honest answer is zero conversions with
 * no cost per conversion.
 */
async function applySelection() {
  const ids = [...state.selection];
  const chart = document.getElementById('conv-chart');
  chart?.classList.add('conv-busy');
  state.busy = true;
  try {
    if (!ids.length) {
      state.conv = { ok: true, has_selection: false, rows: [], actions: [], totals: { conversions: 0, value: 0, all_conversions: 0, all_value: 0 } };
    } else {
      const base = apiBase();
      const res = await guardedFetch(`${base}/m5t/v5/coreAdMetrics/conversions?from=${state.from}&to=${state.to}&actions=${encodeURIComponent(ids.join(','))}`);
      const json = await res.json().catch(() => ({}));
      // There was no status check here at all: on a 403 the error body was assigned
      // straight to state.conv and renderData() ran on it, so a denial rendered as
      // data. Same class as the team-route bug where a 403 showed as "No members yet".
      failIfNotOk(res, json);
      state.conv = json;
    }
    renderData();
  } catch (err) {
    console.warn('[analytics-conversions] recompute failed:', err);
    setHTML('conv-chart', isDenial(err) ? denialState() : emptyState('error', 'Could not recompute for that selection. Please try again.'));
  } finally {
    state.busy = false;
    chart?.classList.remove('conv-busy');
  }
}

// ── rendering ────────────────────────────────────────────────────────────────

/** Tiles, chart and per-action table: everything that moves with the selection. */
function renderData() {
  const conv = state.conv || {};
  const totals = conv.totals || { conversions: 0, value: 0 };
  // all_conversions counts every recorded conversion (AD_CALL phone leads
  // outside the bidding goals included); older payloads predate the field.
  const conversions = Number(totals.all_conversions ?? totals.conversions ?? 0);

  setText('conv-spend', fmtMoney(state.spend));
  setText('conv-count', fmtNum(conversions));
  setText('conv-cpc', fmtMoney(costPer(state.spend, conversions)));
  setText('conv-value', fmtMoney(totals.all_value ?? totals.value ?? 0));

  renderChart(conv.rows || []);
  renderActionTable(conv.actions || []);
}

/**
 * Spend (bars, left) against conversions (line, right). Deliberately the same axis
 * assignment as Ad Performance's "Spend vs Leads": a reseller reading both pages should
 * not have to re-learn which side is money.
 */
function renderChart(convRows) {
  const host = document.getElementById('conv-chart');
  if (!host) return;
  if (!state.selection.size) {
    host.innerHTML = emptyState('checklist', 'No actions counted. Tick a conversion action to see it here.');
    state.rows = [];
    return;
  }
  // The overlay carries a row per day in the window; conversions only carry days with
  // data. Merging on the overlay's spine keeps the x-axis a real calendar instead of a
  // list of days that happened to convert.
  const convByDay = new Map(convRows.map((r) => [String(r.date).slice(0, 10), r]));
  const spine = state.overlayRows.length
    ? state.overlayRows.map((r) => String(r.date).slice(0, 10))
    : convRows.map((r) => String(r.date).slice(0, 10));
  const spendByDay = new Map(state.overlayRows.map((r) => [String(r.date).slice(0, 10), Number(r.spend || 0)]));
  const rows = spine.map((day) => ({
    date: day,
    spend: spendByDay.get(day) || 0,
    conversions: Number(convByDay.get(day)?.all_conversions ?? convByDay.get(day)?.conversions ?? 0),
  }));
  state.rows = rows;
  if (!rows.length) {
    host.innerHTML = emptyState('search_off', 'No conversions in this range for the selected actions.');
    return;
  }
  host.innerHTML = dualAxisChartHTML({
    rows,
    xLabel: (r) => String(r.date || '').slice(5),
    left: { value: (r) => r.spend || 0, label: 'Ad Spend', fmt: (v) => `$${fmtAxis(v)}` },
    right: { value: (r) => r.conversions || 0, label: 'Conversions' },
    ariaLabel: 'Ad spend versus counted conversions over time',
    pointLabel: (r) => `${r.date}: ${fmtMoney(r.spend || 0)} spend, ${fmtNum(r.conversions || 0)} conversions`,
    hitData: (r) => ({ day: r.date }),
  });
  wireChartHover(host, {
    rows,
    tipHTML: (r) => `
      <div class="m5c-tip__title">${esc(String(r.date || ''))}</div>
      <div class="m5c-tip__row"><i class="m5c-sw m5c-sw--1"></i>${fmtMoney(r.spend || 0)} spend</div>
      <div class="m5c-tip__row"><i class="m5c-sw m5c-sw--2"></i>${fmtNum(r.conversions || 0)} conversions</div>`,
  });
}

/** Per-action breakdown of the current selection. Only counted actions appear. */
function renderActionTable(actions) {
  const host = document.getElementById('conv-actions');
  if (!host) return;
  const magnetIds = new Set(state.universe.filter((a) => a.magnet).map((a) => a.id));
  const rows = actions.filter((a) => state.selection.has(String(a.id)));
  if (!rows.length) {
    host.innerHTML = emptyState('search_off', state.selection.size
      ? 'No conversions in this range for the selected actions.'
      : 'No actions counted. Tick a conversion action to see it here.');
    return;
  }
  host.innerHTML = `
    <div class="conv-wrap">
      <table class="conv-table">
        <thead><tr><th>Conversion action</th><th>Category</th><th class="num">All conv.</th><th class="num">Counted</th><th class="num">Value</th></tr></thead>
        <tbody>${rows.map((a) => `
          <tr>
            <td><span class="conv-name">${esc(a.name || a.id)}</span>${magnetIds.has(String(a.id)) ? ' <span class="conv-tag">Magnet</span>' : ''}</td>
            <td class="sub">${esc(prettyCategory(a.category))}</td>
            <td class="num"><b>${fmtNum(a.all_conversions ?? a.conversions)}</b></td>
            <td class="num">${fmtNum(a.conversions)}</td>
            <td class="num">${fmtMoney(a.all_value ?? a.value ?? 0)}</td>
          </tr>`).join('')}
        </tbody>
      </table>
    </div>`;
  const tb = host.querySelector('.conv-table tbody');
  if (tb) capList(tb, { rowSelector: 'tr', noun: 'actions', controlHost: host.querySelector('.conv-wrap') || host });
}

const prettyCategory = (c) => String(c || '').toLowerCase().replace(/_/g, ' ') || DASH;

/** The tick-box list. Magnet-provisioned actions lead, tagged; the rest follow. */
function renderSelector({ accounts }) {
  const host = document.getElementById('conv-selector');
  if (!host) return;
  if (!accounts.length) {
    host.innerHTML = emptyState('link_off', 'No ad account connected yet. Your agency can connect Google Ads to see conversions here.');
    return;
  }
  if (!state.universe.length) {
    host.innerHTML = `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">checklist</span>
      <p>No conversion actions yet. Set them up in <a href="#/settings/integrations/google-ads">Settings → Integrations → Google Ads</a>.</p></div>`;
    return;
  }
  const magnet = state.universe.filter((a) => a.magnet);
  const rest = state.universe.filter((a) => !a.magnet);
  const rowHTML = (a) => `
    <label class="conv-row">
      <input type="checkbox" data-action-id="${esc(a.id)}" value="${esc(a.id)}"${state.selection.has(a.id) ? ' checked' : ''}>
      <span class="conv-name">${esc(a.name || a.id)}</span>
      ${a.magnet ? '<span class="conv-tag">Magnet</span>' : ''}
      ${a.category ? `<span class="conv-cat">${esc(prettyCategory(a.category))}</span>` : ''}
    </label>`;
  host.innerHTML = `
    ${magnet.length ? `<div class="conv-group">Provisioned by Magnet</div><div class="conv-list" id="conv-list-magnet">${magnet.map(rowHTML).join('')}</div>` : ''}
    ${rest.length ? `<div class="conv-group">Other actions in this account</div><div class="conv-list" id="conv-list-other">${rest.map(rowHTML).join('')}</div>` : ''}
    ${state.canSave ? `
      <div class="conv-saverow">
        <button class="btn btn-primary" type="button" id="conv-save">
          <span class="material-symbols-outlined" aria-hidden="true">save</span>Save as default
        </button>
        <span class="conv-savemsg" id="conv-savemsg"></span>
      </div>` : ''}`;
  // Google Ads accounts can carry long action lists. Capped like every other list; hidden
  // checkboxes are still read on save, so a tick made before expanding is never dropped.
  const other = host.querySelector('#conv-list-other');
  if (other) capList(other, { rowSelector: '.conv-row', noun: 'actions' });
  document.getElementById('conv-save')?.addEventListener('click', saveSelection);
}

function updateSelCount() {
  const el = document.getElementById('conv-selcount');
  if (!el) return;
  const n = state.selection.size;
  el.textContent = n ? `${n} action${n === 1 ? '' : 's'} counted` : 'No actions counted';
}

/**
 * Persist the current selection as this org's default. Feedback is inline rather than a
 * toast: the UI_TOAST pipe depends on the neodigm toast being present, and a save that
 * silently appears to do nothing is worse here than one extra line of text.
 */
async function saveSelection() {
  const btn = document.getElementById('conv-save');
  const msg = document.getElementById('conv-savemsg');
  const ids = [...state.selection];
  if (btn) btn.disabled = true;
  if (msg) { msg.classList.remove('err'); msg.textContent = 'Saving.'; }
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/google_ads/conversion_actions`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversion_action_ids: ids }),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.error || 'save failed');
    if (msg) msg.textContent = ids.length ? `Saved. ${ids.length} action${ids.length === 1 ? '' : 's'} counted by default.` : 'Saved. No actions counted by default.';
  } catch (err) {
    if (msg) { msg.classList.add('err'); msg.textContent = 'Could not save the selection. Please try again.'; }
  } finally {
    if (btn) btn.disabled = false;
  }
}

/**
 * Our side of the loop: what Magnet uploaded into Google Ads, by feed kind. Never added to
 * the attributed numbers above, and labelled so the difference is readable.
 */
function renderLedger(ledger) {
  const host = document.getElementById('conv-ledger');
  if (!host) return;
  const kinds = ledger?.kinds || [];
  if (!kinds.length) {
    host.innerHTML = emptyState('upload', 'Nothing fed into Google Ads in this range yet.');
    return;
  }
  const label = (k) => MAGNET_KIND_LABEL[k] || String(k || '').replace(/_/g, ' ');
  const order = (k) => {
    const i = MAGNET_KIND_ORDER.indexOf(k.kind);
    return i < 0 ? 99 : i;
  };
  const sorted = [...kinds].sort((a, b) => order(a) - order(b));
  host.innerHTML = `
    ${kinds.some(k => k.blocked > 0) ? '<p class="conv-note">Google conversion delivery needs updated access. <a href="#/settings/integrations/google_ads">Review Google Ads connection →</a></p>' : ''}
    <div class="conv-wrap">
      <table class="conv-table" id="conv-ledger-table">
        <thead><tr><th>Fed</th><th class="num">Sent</th><th class="num">Queued</th><th class="num">Processing</th><th class="num">Needs attention</th><th class="num">Skipped</th></tr></thead>
        <tbody>${sorted.map((k) => `
          <tr data-kind="${esc(k.kind)}">
            <td><span class="conv-name">${esc(label(k.kind))}</span></td>
            <td class="num"><b>${fmtNum(k.sent)}</b></td>
            <td class="num">${fmtNum(Math.max(0, (k.queued || 0) - (k.processing || 0)))}</td>
            <td class="num">${fmtNum(k.processing || 0)}</td>
            <td class="num">${fmtNum(k.failed)}</td>
            <td class="num">${fmtNum(k.skipped)}</td>
          </tr>`).join('')}
        </tbody>
      </table>
    </div>
    <p class="conv-note">Processing means Google has accepted the upload and is checking it. Sent does not guarantee attribution; Google’s conversion counts appear above.</p>`;
}

function setText(id, v) { const el = document.getElementById(id); if (el) el.textContent = v; }
function setHTML(id, v) { const el = document.getElementById(id); if (el) el.innerHTML = v; }

export default { render, init };
